import { isCategoryEnabled } from "@hexling/core";
import { EmptyState, Spinner, TextField } from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import { useContent, useSettings } from "../../data/AppData";
import { CATEGORIES, categoryInfo, GROUP_LABELS, type CategoryInfo } from "./categories";

export function LibraryPage() {
  const content = useContent();
  const settings = useSettings();
  const [q, setQ] = useState("");

  if (content.isPending || !settings) return <Spinner label="Opening the library" />;
  const index = content.data;
  if (!index)
    return (
      <EmptyState glyph="📚" title="The library hasn't downloaded yet">
        Connect to the internet once and every entry will be saved on this device.
      </EmptyState>
    );

  const shelves = CATEGORIES.filter(
    (c) => isCategoryEnabled(settings, c.category) && index.count(c.category) > 0,
  );
  const enabled = shelves.map((c) => c.category);
  const results = q.trim() ? index.search(q, enabled) : [];
  const groups = (Object.keys(GROUP_LABELS) as CategoryInfo["group"][])
    .map((g) => ({ g, shelves: shelves.filter((s) => s.group === g) }))
    .filter((x) => x.shelves.length);

  return (
    <>
      <PageTitle
        title="Library"
        subtitle="Every entry is saved on this device and works offline."
      />
      <search className="library-search">
        <TextField
          label="Search the library"
          type="search"
          placeholder="Lavender, protection, the Tower…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
        />
      </search>

      {q.trim() ? (
        <section aria-live="polite" aria-labelledby="results-heading">
          <h2 id="results-heading" className="hx-sr-only">
            Search results
          </h2>
          {results.length ? (
            <ul className="label-list">
              {results.map((item) => (
                <li key={`${item.category}:${item.slug}`}>
                  <Link
                    className="label-row"
                    to="/library/$category/$slug"
                    params={{ category: item.category, slug: item.slug }}
                  >
                    <span className="label-row__glyph" aria-hidden="true">
                      {categoryInfo(item.category)?.glyph}
                    </span>
                    <span className="label-row__name">{item.name}</span>
                    <span className="label-row__meta">
                      {categoryInfo(item.category)?.singular ?? item.category}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState glyph="🔍" title={`Nothing found for "${q.trim()}"`}>
              Try a shorter word, an intention like "sleep", or check the spelling.
            </EmptyState>
          )}
        </section>
      ) : (
        <>
          {groups.map(({ g, shelves }) => (
            <section key={g} aria-labelledby={`shelf-${g}`} className="shelf">
              <h2 id={`shelf-${g}`}>{GROUP_LABELS[g]}</h2>
              <ul className="label-list">
                {shelves.map((c) => (
                  <li key={c.category}>
                    <Link
                      className="label-row"
                      to="/library/$category"
                      params={{ category: c.category }}
                    >
                      <span className="label-row__glyph" aria-hidden="true">
                        {c.glyph}
                      </span>
                      <span>
                        <span className="label-row__name">{c.label}</span>
                        <span className="label-row__blurb">{c.blurb}</span>
                      </span>
                      <span className="label-row__meta">{index.count(c.category)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <p className="hx-muted">
            Hide shelves you don't use in <Link to="/settings">Settings</Link>.
          </p>
        </>
      )}
    </>
  );
}
